import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { RecordAccessGrant, RecordAccessLevel, UserType } from '@econtractor/types';
import { useToast } from 'context/ToastContext';

export type RecordAccessSave = (
    grants: { userId: number; accessLevel: RecordAccessLevel }[],
) => Promise<boolean>;

interface UseRecordAccessParams {
    grants: RecordAccessGrant[];
    canManage: boolean;
    ownerId?: number;
    onSave: RecordAccessSave;
}

const toPayload = (rows: RecordAccessGrant[]) =>
    rows.map((grant) => ({
        userId: grant.ID,
        accessLevel: grant.accessLevel,
    }));

const grantSignature = (rows: RecordAccessGrant[]) =>
    [...rows]
        .map((grant) => `${grant.ID}:${grant.accessLevel}`)
        .sort()
        .join('|');

const useRecordAccess = ({
    grants,
    canManage,
    ownerId = 0,
    onSave,
}: UseRecordAccessParams) => {
    const { t } = useTranslation(['common']);
    const { showToast } = useToast();
    const [busy, setBusy] = useState(false);
    const [localGrants, setLocalGrants] = useState(grants);
    const grantsKey = grantSignature(grants);

    useEffect(() => {
        setLocalGrants(grants);
    }, [grantsKey]);

    const editRows = localGrants.filter((grant) => grant.accessLevel === 'edit');
    const viewRows = localGrants.filter((grant) => grant.accessLevel === 'view');
    const takenIds = [
        ...localGrants.map((grant) => grant.ID),
        ...(ownerId > 0 ? [ownerId] : []),
    ];

    const persist = async (next: RecordAccessGrant[], previous: RecordAccessGrant[]) => {
        setLocalGrants(next);
        setBusy(true);
        const ok = await onSave(toPayload(next));
        setBusy(false);
        if (!ok) {
            setLocalGrants(previous);
            showToast(t('messError'), 'danger');
            return;
        }
        showToast(t('Users.accessUpdated'), 'success');
    };

    const addUser = (accessLevel: RecordAccessLevel, user: UserType | null) => {
        if (!user?.ID || !canManage) return;
        if (ownerId > 0 && Number(user.ID) === Number(ownerId)) {
            showToast(t('Users.accessOwnerKept'), 'warning');
            return;
        }
        const rest = localGrants.filter((grant) => grant.ID !== user.ID);
        void persist([
            ...rest,
            {
                ID: user.ID,
                userName: user.userName,
                userEmail: user.userEmail,
                userPicture: user.userPicture || '',
                accessLevel,
            },
        ], localGrants);
    };

    const removeUser = (userId: number) => {
        void persist(localGrants.filter((grant) => grant.ID !== userId), localGrants);
    };

    return {
        busy,
        editRows,
        viewRows,
        takenIds,
        addUser,
        removeUser,
    };
};

export default useRecordAccess;
